import { SimpleOption } from "../../../../../components/input/SimpleListbox";
import {
	DraftHistoryViewModel,
	WriterEssayDraftHistoryViewModel,
} from "./essayDraftHistoryReportApiSlice";

export function isConstructedResponse(
	data: WriterEssayDraftHistoryViewModel[] | undefined
): boolean {
	if (!data) {
		return false;
	}

	return data.flatMap((d) => d.essays)[0]?.isConstructedResponse ?? false;
}

export function getPromptOptions(
	data: WriterEssayDraftHistoryViewModel[] | undefined
): SimpleOption[] {
	const noData: SimpleOption = { value: 0, label: "No Prompts Available" };
	if (!data) {
		return [noData];
	}

	const map = new Map<number, string>();
	data.forEach((writer) => {
		writer.essays?.forEach((essay) => {
			if (!map.has(essay.promptId)) {
				map.set(essay.promptId, essay.promptName);
			}
		});
	});

	if (map.size < 1) {
		return [noData];
	}

	return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
}

export function getFirstPromptOption(data: WriterEssayDraftHistoryViewModel[] | undefined): number {
	return Number(getPromptOptions(data)[0]?.value ?? 0);
}

export function getAverageScoreIncrease(drafts: DraftHistoryViewModel[]): number {
	if (!drafts || drafts.length < 2) {
		return 0;
	}

	const sortedDrafts = [...drafts].sort(
		(a, b) => new Date(a.draftCreateDate).getTime() - new Date(b.draftCreateDate).getTime()
	);

	let totalIncrease = 0;
	let count = 0;
	for (let i = 1; i < sortedDrafts.length; i++) {
		const increase = sortedDrafts[i].draftScore - sortedDrafts[i - 1].draftScore;
		totalIncrease += increase;
		count++;
	}

	return count > 0 ? totalIncrease / count : 0;
}

// If filtered, get the average number of drafts per essay.
// Otherwise, get total drafts for filtered essays.
export function getDraftCount(
	filteredEssays: WriterEssayDraftHistoryViewModel["essays"],
	promptFilter: number
): number {
	if (!filteredEssays || filteredEssays.length === 0) return 0;

	if (promptFilter == 0) {
		const totalDrafts = filteredEssays.reduce(
			(acc, essay) => acc + (essay.drafts?.length ?? 0),
			0
		);
		return Math.round(totalDrafts / filteredEssays.length);
	}

	return filteredEssays.reduce((acc, essay) => acc + (essay.drafts?.length ?? 0), 0);
}

// Get the average score of the class's last draft
export function getLastDraftAverage(
	data: WriterEssayDraftHistoryViewModel[] | undefined,
	promptFilter: number
): number {
	if (!data) {
		return 0;
	}

	const essays = data.flatMap((d) => d.essays);
	const filteredEssays =
		promptFilter == 0
			? essays
			: essays.filter((draft) => Number(draft.promptId) === Number(promptFilter));

	const lastDraftScores = filteredEssays.map((essay) => {
		const drafts = essay.drafts;
		return drafts[drafts.length - 1].draftScore;
	});

	if (lastDraftScores.length === 0) {
		return 0;
	}

	const average = lastDraftScores.reduce((acc, score) => acc + score, 0) / lastDraftScores.length;
	return Number(average.toFixed(1));
}
